import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaUserCog } from "react-icons/fa";
import { validateStaffSetUp, validateField } from "./Staffsetupvalidation";
import styles from "./StaffSetUp.module.css";
import {
  CustomButton,
  CustomDatePicker,
  CustomDropdown,
  CustomFileInput,
  CustomTextInput,
} from "../../../components";
import dayjs from "dayjs";
import AxiosInstance, {
  TokenService,
  toLocalDate,
} from "../../../services/Api";
import PopupModal from "../../../components/PopupModal/PopupModal";
import BlobImage from "../../../components/BlobImage/BlobImage";

const StaffSetUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { staffId, staffData } = location.state || {};
  const user = TokenService.getUser();
  const userId = user?.id || "";
  const clientId = user?.client_data[0].client_id || "";
  const schoolId = user?.client_data[0].school_id || "";
  // Initial form state
  const initialFormData = {
    id: staffId || "",
    rollId: "",
    user_id: "",
    firstName: "",
    middleName: "",
    lastName: "",
    emailAddress: "",
    mobileNumber: "",
    phoneNumber: "",
    designation: "",
    staffType: "",
    teachingCourse: [],
    dateOfJoining: "",
    gender: "",
    bloodGroup: "",
    dateOfBirth: "",
    emergencyContact: "",
    panNumber: "",
    aadharNumber: "",
    maritalStatus: "",
    fatherName: "",
    motherName: "",
    spouseName: "",
    anniversary: "",
    address1: "",
    address2: "",
    address3: "",
    pincode: "",
    city: "",
    state: "",
    country: "",
    staffPhoto: null,
    upload_path: null,
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [teachingCourseOptions, setTeachingCourseOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  // Delete Confirmation Modal
  const [deleteModalState, setDeleteModalState] = useState({
    open: false,
  });

  // Dropdown options
  const staffTypeOptions = [
    { value: "ADMINISTRATIVE", label: "Administrative" },
    { value: "MANAGEMENT", label: "Management" },
    { value: "TEACHING", label: "Teaching" },
  ];

  const genderOptions = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "other", label: "Other" },
  ];

  const bloodGroupOptions = [
    { value: "A+", label: "A+" },
    { value: "A-", label: "A-" },
    { value: "B+", label: "B+" },
    { value: "B-", label: "B-" },
    { value: "AB+", label: "AB+" },
    { value: "AB-", label: "AB-" },
    { value: "O+", label: "O+" },
    { value: "O-", label: "O-" },
    { value: "unknown", label: "Unknown" },
  ];

  const maritalStatusOptions = [
    { value: "single", label: "Single" },
    { value: "married", label: "Married" },
    { value: "divorced", label: "Divorced" },
    { value: "widowed", label: "Widowed" },
  ];

  const fetchCountries = async () => {
    try {
      const response = await AxiosInstance.get("school/countries");

      if (response?.data?.status && Array.isArray(response.data.data)) {
        const countries = response.data.data.map((country) => ({
          value: country.id.toString(),
          label: country.name,
        }));
        setCountryOptions(countries);
      } else {
        setCountryOptions([]);
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
      setCountryOptions([]);
    }
  };

  const fetchStates = async (countryId) => {
    if (!countryId) {
      setStateOptions([]);
      return;
    }

    try {
      setLoadingStates(true);

      const response = await AxiosInstance.post("school/states", {
        country_id: countryId,
      });

      if (response?.data?.status && Array.isArray(response.data.data)) {
        const states = response.data.data.map((state) => ({
          value: state.id.toString(),
          label: state.name,
        }));
        setStateOptions(states);
      } else {
        setStateOptions([]);
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      setStateOptions([]);
    } finally {
      setLoadingStates(false);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let newValue = value;
    if (name === "pincode") {
      const numericValue = value.replace(/\D/g, "").slice(0, 6);

      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));

      return;
    }

    if (
      name === "rollId" ||
      name === "mobileNumber" ||
      name === "emergencyContact"
    ) {
      newValue = value.replace(/\D/g, "").slice(0, 10);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    const fieldError = validateField(name, newValue, formData);
    setErrors((prev) => ({
      ...prev,
      [name]: fieldError,
    }));
  };

  // Safe date parser to prevent RangeError: Invalid time value
  const handleDateChange = (name, date) => {
    const parsedDate = date ? dayjs(date) : null;
    const formattedDate =
      parsedDate && parsedDate.isValid() ? parsedDate.toISOString() : null;

    setFormData((prev) => ({
      ...prev,
      [name]: formattedDate,
    }));

    const fieldError = validateField(name, date, formData);
    setErrors((prev) => ({
      ...prev,
      [name]: fieldError,
    }));
  };

  const handleFileChange = useCallback(async (file, name) => {
    if (!file) {
      setFormData((prev) => ({
        ...prev,
        staffPhoto: null,
        upload_path: null,
      }));

      setErrors((prev) => ({
        ...prev,
        staffPhoto: "",
      }));
      return;
    }

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        staffPhoto: "Only JPG, JPEG, or PNG files are allowed",
      }));
      return;
    }

    try {
      const formDataPayload = new FormData();
      formDataPayload.append("content_image", file);
      formDataPayload.append("type", "StaffPhoto");
      formDataPayload.append("school_id", schoolId);

      const response = await AxiosInstance.post(
        "/content/upload",
        formDataPayload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (!Array.isArray(response.data) || response.data.length === 0) {
        throw new Error("Invalid upload response");
      }

      const uploadedFile = response.data[0];

      setFormData((prev) => ({
        ...prev,
        staffPhoto: uploadedFile.original_name,
        upload_path: uploadedFile.upload_path,
      }));

      setErrors((prev) => ({
        ...prev,
        staffPhoto: "",
      }));
    } catch (error) {
      console.error("Staff photo upload failed:", error);

      setErrors((prev) => ({
        ...prev,
        staffPhoto: "Failed to upload file. Please try again.",
      }));
    }
  }, [schoolId]);

  const handleDropdownChange = (name, value) => {
    let processedValue = value;

    if (Array.isArray(value)) {
      processedValue = value.map((item) => item?.value ?? item);
    } else if (value && typeof value === "object") {
      processedValue = value.value;
    }

    setFormData((prev) => {
      const updatedFormData = {
        ...prev,
        [name]: processedValue,
      };

      if (name === "country") {
        updatedFormData.state = "";
        fetchStates(processedValue);
      }

      if (name === "maritalStatus") {
        if (processedValue === "married") {
          updatedFormData.fatherName = "";
          updatedFormData.motherName = "";
        } else {
          updatedFormData.spouseName = "";
          updatedFormData.anniversary = null;
        }
      }

      if (name === "staffType") {
        if (processedValue !== "TEACHING") {
          updatedFormData.teachingCourse = [];
        }
      }

      const fieldError = validateField(name, processedValue, updatedFormData);
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: fieldError,
      }));

      return updatedFormData;
    });
  };

  // Handle cancel
  const handleCancel = () => {
    navigate(-1);
  };

  // Handle delete button click — show confirmation modal
  const handleDeleteClick = () => {
    setDeleteModalState({ open: true });
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    setDeleteModalState({ open: false });

    if (!formData.user_id) {
      setModalState({
        open: true,
        type: "error",
        message: "Staff user ID not found. Cannot delete.",
        redirectPath: "",
      });
      return;
    }

    setDeleting(true);
    try {
      const response = await AxiosInstance.post("/school/delete-staff", {
        id: formData.id,
      });

      if (response?.data?.status === true) {
        setModalState({
          open: true,
          type: "success",
          message: response?.data?.message || "Staff deleted successfully!",
          redirectPath: -1,
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message ||
            "Failed to delete staff. Please try again.",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error deleting staff:", error);
      const serverError =
        error?.response?.data?.message ||
        "Failed to delete staff. Please try again.";
      setModalState({
        open: true,
        type: "error",
        message: serverError,
        redirectPath: "",
      });
    } finally {
      setDeleting(false);
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteModalState({ open: false });
  };

  const getCourseDropdown = async () => {
    try {
      setSubmitting(true);

      const response = await AxiosInstance.post("/school/listSubject", {
        page: "",
        limit: "",
        search: "",
      });

      if (response?.data?.rows) {
        const options = (response?.data?.rows || []).map((item) => ({
          value: item.id,
          label: item.subject_name,
        }));

        setTeachingCourseOptions(options);
      } else {
        setTeachingCourseOptions([]);
      }
    } catch (error) {
      console.error("Error fetching course dropdown:", error);
      alert("Failed to fetch course list. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGetStaffDetails = async () => {
    if (!staffId) return;

    try {
      setSubmitting(true);

      const response = await AxiosInstance.get(`/school/staff/view/${staffId}`);

      if (response?.data?.success) {
        const staff = response.data.data?.[0];

        if (!staff) return;

        setFormData({
          id: staff.id || staffId,
          user_id: staff.user_id || "",
          rollId: staff.roll_id || "",
          firstName: staff.first_name || "",
          middleName: staff.middle_name || "",
          lastName: staff.last_name || "",
          emailAddress: staff.email || "",
          mobileNumber: staff.mobile_no ? String(staff.mobile_no) : "",
          phoneNumber: staff.phone_no || "",
          designation: staff.designation || "",
          staffType: staff.staff_type || "",
          teachingCourse: staff.subject
            ? staff.subject
                .map((item) => item.subject_id)
                .filter((id) => id != null)
            : [],
          dateOfJoining: staff.doj ? staff.doj.split("T")[0] : "",
          gender: staff.gender || "",
          bloodGroup: staff.blood_group || "",
          dateOfBirth: staff.dob ? staff.dob.split("T")[0] : "",
          emergencyContact: staff.emergency_contact_no || "",
          panNumber: staff.pan_no || "",
          aadharNumber: staff.aadhar_no || "",
          maritalStatus: staff.marital_status || "",
          fatherName: staff.father_name || "",
          motherName: staff.mother_name || "",
          spouseName: staff.spouse_name || "",
          anniversary: staff.anniversary ? staff.anniversary.split("T")[0] : "",
          address1: staff.address1 || "",
          address2: staff.address2 || "",
          address3: staff.address3 || "",
          pincode: staff.postal_code || "",
          city: staff.city || "",
          state: staff.state_id || "",
          country: staff.country_id || "",
          staffPhoto: staff.original_path || null,
          upload_path: staff.staff_photo || null,
        });
        if (staff.country_id) {
          fetchStates(staff.country_id);
        }
      }
    } catch (error) {
      console.error("Error fetching staff details:", error);
      alert("Failed to fetch staff details. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateStaffSetUp(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      const firstErrorField = Object.keys(validationErrors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
      return;
    }

    const formDataPayload = {
      id: formData.id || staffId,
      roll_id: Number(formData.rollId),
      client_id: clientId,
      school_id: schoolId,
      first_name: formData.firstName,
      middle_name: formData.middleName || "",
      last_name: formData.lastName,
      email: formData.emailAddress,
      mobile_no: Number(formData.mobileNumber),
      phone_no: formData.phoneNumber || null,
      designation: formData.designation,
      staff_type: formData.staffType,
      teaching_course:
        formData.staffType === "TEACHING" ? formData.teachingCourse || [] : [],
      gender: formData.gender,
      blood_group: formData.bloodGroup,
      dob: toLocalDate(formData.dateOfBirth),
      doj: toLocalDate(formData.dateOfJoining),
      emergency_contact_no: formData.emergencyContact || null,
      pan_no: formData.panNumber || null,
      aadhar_no: formData.aadharNumber,
      marital_status: formData.maritalStatus,
      spouse_name: formData.spouseName || null,
      anniversary: toLocalDate(formData.anniversary) || null,
      father_name: formData.fatherName,
      mother_name: formData.motherName,
      address1: formData.address1,
      address2: formData.address2 || "",
      address3: formData.address3 || "",
      pincode: formData.pincode,
      city: formData.city,
      state: formData.state,
      country: formData.country || "",
      original_path: formData.staffPhoto,
      staff_photo: formData.upload_path,
      ...(staffId ? { updated_by: userId } : { created_by: userId }),
      user_id: formData.user_id || "",
      isActive: 1,
    };

    setSubmitting(true);
    const url = staffId ? `/school/staff/update` : `/school/staff/create`;
    const method = staffId ? "put" : "post";

    try {
      const response = await AxiosInstance[method](url, formDataPayload);
      if (response?.data?.status === true) {
        setModalState({
          open: true,
          type: "success",
          message: staffId
            ? "Staff updated successfully!"
            : "Staff created successfully!",
          redirectPath: -1,
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.error ||
            response?.data?.message ||
            "Staff creation failed",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      const serverError =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to create staff. Please try again.";

      alert(serverError);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    getCourseDropdown();
    fetchCountries();
    if (staffId) {
      handleGetStaffDetails();
    }
  }, [staffId]);

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      if (redirectPath === -1) {
        navigate(-1);
      }
    }
  };

  return (
    <>
      {/* Success/Error Modal */}
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />

      {/* Delete Confirmation Modal */}
      <PopupModal
        open={deleteModalState.open}
        type="warning"
        title="Confirm Delete"
        message="Are you sure you want to delete this staff? This action cannot be undone."
        onOk={handleDeleteConfirm}
        onClose={handleDeleteCancel}
      />

      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaUserCog />
          </div>
          <h1 className={styles.headerTitle}>Staff Setup</h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className={styles.formContainer}>
          {/* Row 1*/}
          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="Roll Id"
                name="rollId"
                type="text"
                value={formData.rollId}
                onChange={handleInputChange}
                placeholder="E.g. 10102004001"
                error={errors.rollId}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="First Name"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="E.g. First Name"
                required
                error={errors.firstName}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="Middle Name"
                name="middleName"
                type="text"
                value={formData.middleName}
                onChange={handleInputChange}
                placeholder="E.g. Middle Name"
                error={errors.middleName}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="Last Name"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="E.g. Last Name"
                required
                error={errors.lastName}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="Email Address"
                name="emailAddress"
                type="email"
                value={formData.emailAddress}
                onChange={handleInputChange}
                placeholder="E.g. staff@gmail.com"
                required
                error={errors.emailAddress}
                disabled={!!staffId}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="Mobile Number"
                name="mobileNumber"
                type="number"
                value={formData.mobileNumber}
                onChange={handleInputChange}
                placeholder="E.g. Mobile Number"
                inputClassName={styles.markInput}
                required
                error={errors.mobileNumber}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="Phone number"
                name="phoneNumber"
                type="number"
                inputClassName={styles.markInput}
                value={formData.phoneNumber}
                onChange={handleInputChange}
                placeholder="E.g. Phone number"
                error={errors.phoneNumber}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="Designation"
                name="designation"
                type="text"
                value={formData.designation}
                onChange={handleInputChange}
                placeholder="E.g. Designation"
                error={errors.designation}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label="Staff Type"
                name="staffType"
                value={formData.staffType}
                onChange={(value) => handleDropdownChange("staffType", value)}
                options={staffTypeOptions}
                placeholder="Select type"
                required
                error={errors.staffType}
              />
            </div>

            {formData.staffType === "TEACHING" && (
              <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                <CustomDropdown
                  label="Teaching Course"
                  name="teachingCourse"
                  value={formData.teachingCourse}
                  onChange={(value) =>
                    handleDropdownChange("teachingCourse", value)
                  }
                  options={teachingCourseOptions}
                  placeholder="Select course"
                  required
                  error={errors.teachingCourse}
                  isMulti={true}
                  closeMenuOnSelect={false}
                />
              </div>
            )}

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label="Date Of Joining"
                name="dateOfJoining"
                value={
                  formData.dateOfJoining && dayjs(formData.dateOfJoining).isValid()
                    ? dayjs(formData.dateOfJoining)
                    : null
                }
                onChange={(date) => handleDateChange("dateOfJoining", date)}
                placeholder="Select Date"
                required
                error={errors.dateOfJoining}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label="Gender"
                name="gender"
                value={formData.gender}
                onChange={(value) => handleDropdownChange("gender", value)}
                options={genderOptions}
                placeholder="Select Gender"
                required
                error={errors.gender}
              />
            </div>
          </div>

          {/* Row 2*/}
          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label="Blood Group"
                name="bloodGroup"
                value={formData.bloodGroup}
                onChange={(value) => handleDropdownChange("bloodGroup", value)}
                options={bloodGroupOptions}
                placeholder="Select Blood Group"
                required
                error={errors.bloodGroup}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label="Date Of Birth"
                name="dateOfBirth"
                value={
                  formData.dateOfBirth && dayjs(formData.dateOfBirth).isValid()
                    ? dayjs(formData.dateOfBirth)
                    : null
                }
                onChange={(date) => handleDateChange("dateOfBirth", date)}
                placeholder="Select Date"
                required
                error={errors.dateOfBirth}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="Emergency Contact Number"
                name="emergencyContact"
                type="tel"
                value={formData.emergencyContact}
                onChange={handleInputChange}
                placeholder="E.g. Mobile no"
                error={errors.emergencyContact}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="PAN Number"
                name="panNumber"
                type="text"
                value={formData.panNumber}
                onChange={handleInputChange}
                placeholder="E.g. PAN Number"
                error={errors.panNumber}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="Aadhar number"
                name="aadharNumber"
                type="text"
                value={formData.aadharNumber}
                onChange={handleInputChange}
                placeholder="E.g. Aadhar number"
                required
                error={errors.aadharNumber}
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label="Marital Status"
                name="maritalStatus"
                value={formData.maritalStatus}
                onChange={(value) =>
                  handleDropdownChange("maritalStatus", value)
                }
                options={maritalStatusOptions}
                placeholder="Select Marital Status"
                required
                error={errors.maritalStatus}
              />
            </div>
            {formData.maritalStatus === "married" ? (
              <>
                <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                  <CustomTextInput
                    label="Spouse Name"
                    name="spouseName"
                    type="text"
                    value={formData.spouseName}
                    onChange={handleInputChange}
                    placeholder="E.g. John"
                    error={errors.spouseName}
                  />
                </div>

                <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                  <CustomDatePicker
                    label="Anniversary Date"
                    name="anniversary"
                    value={
                      formData.anniversary && dayjs(formData.anniversary).isValid()
                        ? dayjs(formData.anniversary)
                        : null
                    }
                    onChange={(date) => handleDateChange("anniversary", date)}
                    placeholder="Select Date"
                    required
                    error={errors.anniversary}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                  <CustomTextInput
                    label="Father Name"
                    name="fatherName"
                    type="text"
                    value={formData.fatherName}
                    onChange={handleInputChange}
                    placeholder="E.g. Mary"
                    error={errors.fatherName}
                  />
                </div>
                <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                  <CustomTextInput
                    label="Mother Name"
                    name="motherName"
                    type="text"
                    value={formData.motherName}
                    onChange={handleInputChange}
                    placeholder="E.g. Elizabeth"
                    error={errors.motherName}
                  />
                </div>
              </>
            )}
          </div>

          {/* Row 3 */}
          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="Address 1"
                name="address1"
                type="text"
                value={formData.address1}
                onChange={handleInputChange}
                placeholder="E.g. Address"
                required
                error={errors.address1}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="Address 2"
                name="address2"
                type="text"
                value={formData.address2}
                onChange={handleInputChange}
                placeholder="E.g. Address"
                error={errors.address2}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="Address 3"
                name="address3"
                type="text"
                value={formData.address3}
                onChange={handleInputChange}
                placeholder="E.g. Address"
                error={errors.address3}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="Pincode"
                name="pincode"
                type="text"
                value={formData.pincode}
                onChange={handleInputChange}
                placeholder="E.g. Pincode"
                required
                error={errors.pincode}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="City"
                name="city"
                type="text"
                value={formData.city}
                onChange={handleInputChange}
                placeholder="E.g. City"
                required
                error={errors.city}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label="State"
                name="state"
                value={formData.state}
                onChange={(value) => handleDropdownChange("state", value)}
                options={stateOptions}
                placeholder={
                  loadingStates
                    ? "Loading states..."
                    : formData.country
                      ? "Select State"
                      : "Select country first"
                }
                required
                error={errors.state}
                isLoading={loadingStates}
                isDisabled={!formData.country || loadingStates}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label="Country"
                name="country"
                value={formData.country}
                onChange={(value) => handleDropdownChange("country", value)}
                options={countryOptions}
                placeholder="Select Country"
                required
                error={errors.country}
                isLoading={submitting && !countryOptions.length}
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomFileInput
                label="Staff Photo"
                name="staffPhoto"
                value={formData.staffPhoto}
                onChange={handleFileChange}
                accept="image/*"
                placeholder={
                  typeof formData.staffPhoto === "string" && formData.staffPhoto
                    ? formData.staffPhoto.split("/").pop()
                    : "E.g. Mypic.jpg"
                }
                buttonText="Choose File"
                error={errors.staffPhoto}
              />
              {formData.upload_path && (
                <div style={{ marginTop: "8px" }}>
                  <BlobImage
                    path={formData.upload_path}
                    alt="Staff photo preview"
                    style={{
                      width: 96,
                      height: 96,
                      objectFit: "cover",
                      borderRadius: 8,
                      border: "1px solid #dcdcdc",
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className={styles.buttonContainer}>
            {staffId && (
              <CustomButton
                type="button"
                variant="contained"
                className={styles.deleteButton}
                fullWidth={false}
                onClick={handleDeleteClick}
                disabled={deleting}
                loading={deleting}
                loadingText="Deleting..."
              >
                DELETE
              </CustomButton>
            )}

            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              CANCEL
            </CustomButton>

            <CustomButton
              type="submit"
              variant="contained"
              className={styles.submitButton}
              fullWidth={false}
              loading={submitting}
              loadingText="Submitting..."
            >
              SUBMIT
            </CustomButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default StaffSetUp;